import { drawStamp, formatStampText } from '@/lib/stamp'

const MAX_DIMENSION = 3072
const IMAGE_QUALITY = 0.9
const THUMB_MAX_EDGE = 300
const THUMB_QUALITY = 0.6

type EncodeFormat = { mime: 'image/webp' | 'image/jpeg'; ext: '.webp' | '.jpg' }

const WEBP: EncodeFormat = { mime: 'image/webp', ext: '.webp' }
const JPEG: EncodeFormat = { mime: 'image/jpeg', ext: '.jpg' }
const FORMATS = { 'image/webp': WEBP, 'image/jpeg': JPEG } as const

let formatPromise: Promise<EncodeFormat> | null = null

// Some Safari/iOS app contexts accept canvas.toBlob('image/webp', q) without
// erroring but silently emit PNG. Detect once by checking the actual blob.type
// of a 1×1 probe — if it isn't WebP, fall back to JPEG so we never ship an
// uncompressed PNG masquerading as WebP.
function detectFormat(): Promise<EncodeFormat> {
  if (formatPromise) return formatPromise
  formatPromise = new Promise((resolve) => {
    try {
      const probe = document.createElement('canvas')
      probe.width = 1
      probe.height = 1
      probe.toBlob(
        (blob) => resolve(blob && blob.type === 'image/webp' ? WEBP : JPEG),
        'image/webp',
        0.9,
      )
    } catch {
      resolve(JPEG)
    }
  })
  return formatPromise
}

void detectFormat()

export interface CompressResult {
  file: File
  /** Tiny WebP data URL for instant offline preview. Null on encode failure. */
  thumbDataUrl: string | null
}

export interface CompressOptions {
  /**
   * Burn a small capture-time date+time watermark into the image (feature #3 —
   * Zeitstempel im Bild). OFF by default so non-photo uploads (floor plans,
   * AI-training images) stay clean; defect-photo flows opt in.
   *
   * Drawn inside the compression worker on the already-downscaled canvas, so
   * bulk drops stay off the main thread.
   */
  stamp?: boolean
}

// Worker pool: bulk drops froze the UI for tens of seconds on main-thread.
// Falls back to main-thread when OffscreenCanvas / createImageBitmap aren't
// available (older Safari).

interface PoolWorker {
  worker: Worker
  inFlight: number
}

let workerPool: PoolWorker[] | null = null
let workerSupportProbed = false
let workerSupported = false
let probePromise: Promise<boolean> | null = null

function pickPoolSize(): number {
  if (typeof navigator === 'undefined') return 2
  // Cap at 3 to avoid memory pressure on mobile — each worker can hold a
  // multi-MB bitmap during a decode. hardwareConcurrency reports logical
  // cores; on iPhone that's typically 6 (2P+4E), but ramming all of them
  // into image decode just thrashes memory.
  return Math.max(1, Math.min(3, (navigator.hardwareConcurrency || 2) - 1))
}

async function probeWorkerSupport(): Promise<boolean> {
  if (workerSupportProbed) return workerSupported
  if (probePromise) return probePromise
  probePromise = (async () => {
    try {
      if (typeof Worker === 'undefined') return false
      if (typeof OffscreenCanvas === 'undefined') return false
      if (typeof createImageBitmap === 'undefined') return false
      // OffscreenCanvas with 2d context support shipped late on Safari (16.4);
      // earlier versions return null from getContext('2d'). Probe before
      // committing to the worker path.
      const probe = new OffscreenCanvas(1, 1)
      if (!probe.getContext('2d')) return false
      return true
    } catch {
      return false
    } finally {
      workerSupportProbed = true
    }
  })()
  workerSupported = await probePromise
  return workerSupported
}

function getPool(): PoolWorker[] {
  if (workerPool) return workerPool
  const size = pickPoolSize()
  workerPool = []
  for (let i = 0; i < size; i++) {
    try {
      const w = new Worker(
        new URL('../workers/imageCompressWorker.ts', import.meta.url),
        { type: 'module' },
      )
      workerPool.push({ worker: w, inFlight: 0 })
    } catch { /* surviving workers still usable; empty pool → main-thread fallback */ }
  }
  return workerPool
}

function pickWorker(pool: PoolWorker[]): PoolWorker {
  let pick = pool[0]
  for (let i = 1; i < pool.length; i++) {
    if (pool[i].inFlight < pick.inFlight) pick = pool[i]
  }
  return pick
}

interface WorkerOk {
  id: string
  ok: true
  blob: Blob
  blobType: string
  width: number
  height: number
  thumbDataUrl: string | null
}
interface WorkerErr { id: string; ok: false; error: string }

function compressInWorker(
  source: File | ImageBitmap,
  fileName: string,
  fileType: string,
  format: EncodeFormat,
  stampText?: string,
): Promise<CompressResult> | null {
  const pool = getPool()
  if (pool.length === 0) return null
  const slot = pickWorker(pool)
  const id = crypto.randomUUID()
  slot.inFlight++
  const isBitmap = typeof ImageBitmap !== 'undefined' && source instanceof ImageBitmap
  return new Promise<CompressResult>((resolve, reject) => {
    let settled = false
    const cleanup = () => {
      if (settled) return
      settled = true
      slot.worker.removeEventListener('message', onMessage)
      slot.worker.removeEventListener('error', onError)
      slot.inFlight--
    }
    const onMessage = (e: MessageEvent<WorkerOk | WorkerErr>) => {
      if (e.data.id !== id) return
      cleanup()
      if (!e.data.ok) {
        reject(new Error(e.data.error))
        return
      }
      const out = e.data
      const chosen = FORMATS[out.blobType as keyof typeof FORMATS] ?? format
      const compressed = new File(
        [out.blob],
        fileName.replace(/\.\w+$/, chosen.ext),
        { type: chosen.mime, lastModified: Date.now() },
      )
      const srcSize = isBitmap ? null : (source as File).size
      console.log(
        `Image compressed (worker): ${srcSize != null ? `${(srcSize / 1024 / 1024).toFixed(2)}MB` : 'bitmap'} → ${(compressed.size / 1024 / 1024).toFixed(2)}MB ` +
        `(→ ${out.width}x${out.height}, ${chosen.mime})`,
      )
      resolve({ file: compressed, thumbDataUrl: out.thumbDataUrl })
    }
    // Without this, a worker that crashes mid-job leaks both the listener and
    // the slot.inFlight increment forever, slowly draining the pool's capacity.
    const onError = (e: ErrorEvent | MessageEvent) => {
      cleanup()
      reject(new Error(`worker error: ${'message' in e && e.message ? e.message : 'unknown'}`))
    }
    slot.worker.addEventListener('message', onMessage)
    slot.worker.addEventListener('error', onError)
    // Transfer the bitmap (zero-copy) so the worker owns it and the main thread
    // pays nothing to hand off the decoded frame.
    slot.worker.postMessage(
      {
        id,
        blob: isBitmap ? undefined : source,
        bitmap: isBitmap ? source : undefined,
        fileName,
        fileType,
        format: format.mime,
        maxDimension: MAX_DIMENSION,
        imageQuality: IMAGE_QUALITY,
        thumbMaxEdge: THUMB_MAX_EDGE,
        thumbQuality: THUMB_QUALITY,
        stampText,
      },
      isBitmap ? [source as ImageBitmap] : [],
    )
  })
}

/**
 * Decode the source image once and fan out to both the compressed file and a
 * tiny preview thumbnail data URL from the same canvas. Used at the capture
 * boundary so the IDB offlineQueue blob and the wire payload are small AND
 * the local-thumb preview is ready without a second decode.
 *
 * Runs in a worker pool when the browser supports OffscreenCanvas +
 * createImageBitmap (modern Chrome/Firefox/Safari 16.4+). Falls back to a
 * main-thread `<img>` + `<canvas>` path otherwise — same fan-out, same output.
 *
 * Resizes to max 3072px and encodes WebP@0.9 when the browser supports WebP,
 * otherwise JPEG@0.9. Thumb is always WebP@0.6 at max 300px edge.
 */
export async function compressImageWithThumb(
  input: File | Blob | ImageBitmap,
  options: CompressOptions = {},
): Promise<CompressResult> {
  // Capture-time timestamp (feature #3). Computed HERE, at capture time, then
  // drawn on the already-downscaled canvas inside the worker — stamping a
  // full-res image on the main thread first is what froze bulk drops.
  const stampText = options.stamp ? formatStampText() : undefined
  // Normalise: a transferred ImageBitmap (capture fast-path) stays a bitmap;
  // everything else becomes a File so the rest narrows cleanly.
  let bitmap: ImageBitmap | null = null
  let sourceFile: File | null = null
  if (input instanceof ImageBitmap) {
    bitmap = input
  } else if (input instanceof File) {
    sourceFile = input
  } else {
    sourceFile = new File([input], 'capture', { type: input.type, lastModified: Date.now() })
  }
  const fileName = sourceFile?.name ?? 'capture'
  const fileType = sourceFile?.type ?? 'image/jpeg'

  const format = await detectFormat()

  if (await probeWorkerSupport()) {
    const workerPromise = compressInWorker(bitmap ?? sourceFile!, fileName, fileType, format, stampText)
    if (workerPromise) {
      try {
        return await workerPromise
      } catch (err) {
        console.warn('Image compression worker failed, falling back to main thread', err)
        // fall through to main-thread path
      }
    }
  }

  // Main-thread fallback (worker unsupported / failed). For a bitmap, draw it to
  // a canvas; for a blob/file, decode via <img>.
  return bitmap
    ? compressBitmapOnMainThread(bitmap, format, stampText)
    : compressOnMainThread(sourceFile!, format, stampText)
}

// Main-thread fallback for the capture fast-path when the worker pool is
// unavailable. Draws the already-decoded bitmap to a canvas and encodes there.
// Same slow `canvas.toBlob` as compressOnMainThread — acceptable because this
// only runs when OffscreenCanvas/worker support is missing (rare on modern
// Android/iOS); the worker path skips it entirely.
function compressBitmapOnMainThread(bitmap: ImageBitmap, format: EncodeFormat, stampText?: string): Promise<CompressResult> {
  let { width, height } = bitmap
  if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
    const ratio = Math.min(MAX_DIMENSION / width, MAX_DIMENSION / height)
    width = Math.round(width * ratio)
    height = Math.round(height * ratio)
  }

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) return Promise.reject(new Error('2d-context-unavailable'))
  ctx.drawImage(bitmap, 0, 0, width, height)
  if (stampText) {
    try { drawStamp(ctx, width, height, stampText) } catch { /* stamp is best-effort */ }
  }

  let thumbDataUrl: string | null = null
  try {
    const tScale = Math.min(1, THUMB_MAX_EDGE / Math.max(width, height))
    const tw = Math.max(1, Math.round(width * tScale))
    const th = Math.max(1, Math.round(height * tScale))
    const thumbCanvas = document.createElement('canvas')
    thumbCanvas.width = tw
    thumbCanvas.height = th
    const tctx = thumbCanvas.getContext('2d')
    if (tctx) {
      tctx.drawImage(canvas, 0, 0, tw, th)
      thumbDataUrl = thumbCanvas.toDataURL('image/webp', THUMB_QUALITY)
    }
  } catch { /* thumb is best-effort */ }

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) { reject(new Error('toBlob failed')); return }
        const chosen = FORMATS[blob.type as keyof typeof FORMATS] ?? format
        const compressed = new File(
          [blob],
          `capture${chosen.ext}`,
          { type: chosen.mime, lastModified: Date.now() },
        )
        console.log(
          `Image compressed (main-thread bitmap): → ${(compressed.size / 1024 / 1024).toFixed(2)}MB ` +
          `(${width}x${height}, ${chosen.mime})`,
        )
        resolve({ file: compressed, thumbDataUrl })
      },
      format.mime,
      IMAGE_QUALITY,
    )
  })
}

function compressOnMainThread(sourceFile: File, format: EncodeFormat, stampText?: string): Promise<CompressResult> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const url = URL.createObjectURL(sourceFile)

    img.onload = () => {
      URL.revokeObjectURL(url)

      let { width, height } = img
      if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
        const ratio = Math.min(MAX_DIMENSION / width, MAX_DIMENSION / height)
        width = Math.round(width * ratio)
        height = Math.round(height * ratio)
      }

      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height

      const ctx = canvas.getContext('2d')
      if (!ctx) {
        resolve({ file: sourceFile, thumbDataUrl: null })
        return
      }

      ctx.drawImage(img, 0, 0, width, height)
      if (stampText) {
        try { drawStamp(ctx, width, height, stampText) } catch { /* stamp is best-effort */ }
      }

      // Derive the thumb from the just-drawn full-size canvas — the decoded
      // pixels are already in GPU memory so this is a single drawImage scale,
      // not another decode. Best-effort: a failed thumb just means the
      // SignedImage component falls back to the remote thumbnail.
      let thumbDataUrl: string | null = null
      try {
        const tScale = Math.min(1, THUMB_MAX_EDGE / Math.max(width, height))
        const tw = Math.max(1, Math.round(width * tScale))
        const th = Math.max(1, Math.round(height * tScale))
        const thumbCanvas = document.createElement('canvas')
        thumbCanvas.width = tw
        thumbCanvas.height = th
        const tctx = thumbCanvas.getContext('2d')
        if (tctx) {
          tctx.drawImage(canvas, 0, 0, tw, th)
          thumbDataUrl = thumbCanvas.toDataURL('image/webp', THUMB_QUALITY)
        }
      } catch { /* thumb is best-effort */ }

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            resolve({ file: sourceFile, thumbDataUrl })
            return
          }
          const chosen = FORMATS[blob.type as keyof typeof FORMATS] ?? format
          const compressed = new File(
            [blob],
            sourceFile.name.replace(/\.\w+$/, chosen.ext),
            { type: chosen.mime, lastModified: Date.now() },
          )
          console.log(
            `Image compressed: ${(sourceFile.size / 1024 / 1024).toFixed(2)}MB → ${(compressed.size / 1024 / 1024).toFixed(2)}MB ` +
            `(${img.naturalWidth}x${img.naturalHeight} → ${width}x${height}, ${chosen.mime})`,
          )
          resolve({ file: compressed, thumbDataUrl })
        },
        format.mime,
        IMAGE_QUALITY,
      )
    }

    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Failed to load image for compression'))
    }

    img.src = url
  })
}

/**
 * Compress-only convenience wrapper. Prefer `compressImageWithThumb` when the
 * caller also needs a local-thumb preview — that returns both in one decode.
 */
export async function compressImage(file: File | Blob): Promise<File> {
  const { file: compressed } = await compressImageWithThumb(file)
  return compressed
}
